import { type SortingState, type OnChangeFn } from '@tanstack/react-table';
import { type CSSProperties, useCallback, useMemo } from 'react';

import { DataTable } from '@/components/ui/data-table';
import type { OpportunityListItem } from '@/hooks/api/useOpportunities';

import { getOpportunityColumns, getPinnedRightColumns } from './opportunity-columns';

function getScoreColor(score: number): string {
  if (score >= 80) return 'var(--success)';
  if (score >= 50) return 'var(--warning)';
  return 'var(--destructive)';
}

interface OpportunityTableProps {
  data: OpportunityListItem[];
  isLoading?: boolean;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  onRowClick?: (row: OpportunityListItem) => void;
  sentToAgentPlatform?: boolean;
}

export function OpportunityTable({
  data,
  isLoading,
  sorting,
  onSortingChange,
  onRowClick,
  sentToAgentPlatform = false,
}: OpportunityTableProps) {
  const columns = useMemo(
    () => getOpportunityColumns({ sentToAgentPlatform }),
    [sentToAgentPlatform]
  );
  const pinnedRightColumns = useMemo(
    () => getPinnedRightColumns(sentToAgentPlatform),
    [sentToAgentPlatform]
  );
  const rowStyle = useCallback(
    (row: OpportunityListItem): CSSProperties => ({
      borderLeft: `3px solid hsl(${getScoreColor(row.propensityScore)} / 0.6)`,
    }),
    []
  );

  return (
    <DataTable
      columns={columns}
      data={data}
      isLoading={isLoading}
      sorting={sorting}
      onSortingChange={onSortingChange}
      onRowClick={onRowClick}
      rowStyle={rowStyle}
      pinnedRightColumns={pinnedRightColumns}
      stickyHeader
      scrollHint
      maxHeight="calc(100svh - 18rem)"
      emptyMessage="No opportunities match your current filters."
    />
  );
}
